import { useRef, useState, type ChangeEvent } from 'react';
import type { Manifest } from '../content/data';
import { navigate } from '../navigation/routes';
import { completedDayCount, parseProgressImport, serializeProgress } from '../progress/progress';
import { useProgress, useProgressActions } from '../progress/progress-context';

type Notice = { kind: 'success' | 'error'; text: string } | null;

export function SettingsPage({ manifest }: { manifest: Manifest }) {
  const progress = useProgress();
  const { replaceProgress, resetProgress } = useProgressActions();
  const [notice, setNotice] = useState<Notice>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const exportProgress = () => {
    const blob = new Blob([serializeProgress(progress)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'frontend-fieldbook-progress.json';
    anchor.click();
    URL.revokeObjectURL(url);
    setNotice({ kind: 'success', text: 'Progress backup downloaded.' });
  };

  const importProgress = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;

    try {
      const imported = parseProgressImport(await file.text());
      replaceProgress(imported);
      setNotice({ kind: 'success', text: 'Progress restored from the selected file.' });
    } catch {
      setNotice({ kind: 'error', text: 'That file is not a valid Frontend Fieldbook progress backup.' });
    }
  };

  const reset = () => {
    resetProgress();
    navigate({ page: 'today' });
  };

  return (
    <main className="settings-page" id="main-content">
      <header className="page-heading">
        <h1>Settings and progress</h1>
        <p>The app stores progress in this browser. Export a backup before clearing browser data or changing devices.</p>
      </header>

      {notice ? <p className={`notice notice--${notice.kind}`} role="status">{notice.text}</p> : null}

      <section className="settings-section">
        <div>
          <h2>Backup</h2>
          <p>The JSON file contains your profile, completion dates, checklists, interview notes, and reflections.</p>
        </div>
        <div className="settings-actions">
          <button className="primary-button" type="button" onClick={exportProgress}>Export progress</button>
          <button className="secondary-button" type="button" onClick={() => fileInput.current?.click()}>Import progress</button>
          <input
            className="visually-hidden-input"
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            aria-label="Import a progress JSON file"
            onChange={(event) => void importProgress(event)}
          />
        </div>
      </section>

      <section className="settings-section">
        <div>
          <h2>Current data</h2>
          <dl className="settings-data">
            <div><dt>Days completed</dt><dd>{completedDayCount(manifest, progress)} / {manifest.totals.days}</dd></div>
            <div><dt>Questions attempted</dt><dd>{Object.values(progress.questionAttempts).filter((attempt) => attempt.attempted).length}</dd></div>
            <div><dt>Started</dt><dd>{progress.startedAt ? new Date(progress.startedAt).toLocaleDateString() : 'Not started'}</dd></div>
          </dl>
        </div>
      </section>

      {!manifest.completeCurriculum ? (
        <section className="settings-section sample-data-note">
          <div>
            <h2>Review data</h2>
            <p>This checkout contains Weeks 1 and 2 for frontend validation. Generate the full curriculum before publishing it for a learner.</p>
            <code>npm run content:build</code>
          </div>
        </section>
      ) : null}

      <section className="settings-section danger-section">
        <div>
          <h2>Start over</h2>
          <p>This clears progress from the browser and opens onboarding again. Export first if you may need the record.</p>
        </div>
        {confirmingReset ? (
          <div className="reset-confirmation">
            <p>Clear every saved answer and completed day?</p>
            <button className="secondary-button" type="button" onClick={() => setConfirmingReset(false)}>Cancel</button>
            <button className="danger-button" type="button" onClick={reset}>Clear progress</button>
          </div>
        ) : (
          <button className="danger-button" type="button" onClick={() => setConfirmingReset(true)}>Reset all progress</button>
        )}
      </section>

      <details className="build-details">
        <summary>Build details</summary>
        <dl>
          <div><dt>Content schema</dt><dd>v{manifest.schemaVersion}</dd></div>
          <div><dt>Source digest</dt><dd><code>{manifest.sourceDigest}</code></dd></div>
          <div><dt>Weeks loaded</dt><dd>{manifest.selectedWeekNumbers.join(', ')}</dd></div>
        </dl>
      </details>
    </main>
  );
}
